import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

interface UseCanvasViewportOptions {
  containerRef: RefObject<HTMLDivElement>;
  canvasRef: RefObject<HTMLCanvasElement>;
  canvasWidth: number;
  canvasHeight: number;
  backgroundImageScale: number;
  backgroundImageOffset: { x: number; y: number };
  onScaleChange: (scale: number) => void;
  onOffsetChange: (offset: { x: number; y: number }) => void;
  hasBackgroundImage: boolean;
  isElementSelected: boolean;
  isDraggingElement: boolean;
  isStickerModeActive: boolean;
}

const MIN_SCALE = 0.1;
const MAX_SCALE = 5;
const ZOOM_SENSITIVITY = 0.002;
const PINCH_SENSITIVITY = 0.01;

export function useCanvasViewport(options: UseCanvasViewportOptions) {
  const {
    containerRef,
    canvasRef,
    canvasWidth,
    canvasHeight,
    backgroundImageScale,
    backgroundImageOffset,
    onScaleChange,
    onOffsetChange,
    hasBackgroundImage,
    isElementSelected,
    isDraggingElement,
    isStickerModeActive,
  } = options;

  const [isPanning, setIsPanning] = useState(false);
  const [displayScale, setDisplayScale] = useState(1);

  const panStartRef = useRef({ x: 0, y: 0 });
  const offsetStartRef = useRef({ x: 0, y: 0 });
  const lastTouchDistanceRef = useRef<number | null>(null);
  const lastTouchCenterRef = useRef<{ x: number; y: number } | null>(null);

  // Calculate the scale ratio between internal resolution and actual DOM size
  const updateDisplayScale = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    if (canvasWidth > 0 && rect.width > 0) {
      // displayScale = CSS pixels / internal pixels
      // This allows converting internal coordinates to screen coordinates and vice versa
      setDisplayScale(rect.width / canvasWidth);
    }
  }, [canvasRef, canvasWidth]);

  // Set up resize observation to keep displayScale in sync
  useEffect(() => {
    updateDisplayScale();

    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      updateDisplayScale();
    });

    resizeObserver.observe(container);
    window.addEventListener('resize', updateDisplayScale);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateDisplayScale);
    };
  }, [containerRef, updateDisplayScale]);

  // Convert screen coordinates to internal canvas coordinates
  const screenToCanvas = useCallback(
    (screenX: number, screenY: number): { x: number; y: number } => {
      const canvas = canvasRef.current;
      if (!canvas || displayScale === 0) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();

      return {
        x: (screenX - rect.left) / displayScale,
        y: (screenY - rect.top) / displayScale,
      };
    },
    [canvasRef, displayScale]
  );

  // Handle mouse wheel zoom on background image
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (!hasBackgroundImage || isStickerModeActive) return;

      e.preventDefault();

      const delta = -e.deltaY * ZOOM_SENSITIVITY;
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, backgroundImageScale * (1 + delta)));

      // Zoom towards mouse position
      const { x: canvasX, y: canvasY } = screenToCanvas(e.clientX, e.clientY);

      // Calculate the offset to keep the zoom centered on the mouse position
      const centerX = canvasWidth / 2;
      const centerY = canvasHeight / 2;

      // Vector from center to mouse position
      const dx = canvasX - centerX;
      const dy = canvasY - centerY;

      // Adjust offset to zoom towards mouse
      const scaleRatio = newScale / backgroundImageScale;
      const newOffsetX = backgroundImageOffset.x - dx * (scaleRatio - 1);
      const newOffsetY = backgroundImageOffset.y - dy * (scaleRatio - 1);

      onScaleChange(newScale);
      onOffsetChange({ x: newOffsetX, y: newOffsetY });
    },
    [
      hasBackgroundImage,
      isStickerModeActive,
      backgroundImageScale,
      backgroundImageOffset,
      onScaleChange,
      onOffsetChange,
      screenToCanvas,
      canvasWidth,
      canvasHeight,
    ]
  );

  // Handle mouse down for panning
  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      // Only allow panning with middle mouse button, or left click when no element is selected
      // and we have a background image
      if (!hasBackgroundImage || isStickerModeActive) return;

      const isMiddleButton = e.button === 1;
      const isLeftClickWithAlt = e.button === 0 && e.altKey;
      const isLeftClickNoSelection = e.button === 0 && !isElementSelected && !isDraggingElement;

      if (isMiddleButton || isLeftClickWithAlt || isLeftClickNoSelection) {
        e.preventDefault();
        setIsPanning(true);
        panStartRef.current = { x: e.clientX, y: e.clientY };
        offsetStartRef.current = { ...backgroundImageOffset };
      }
    },
    [hasBackgroundImage, isStickerModeActive, isElementSelected, isDraggingElement, backgroundImageOffset]
  );

  // Handle mouse move for panning
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isPanning || !hasBackgroundImage || displayScale === 0) return;

      const deltaX = (e.clientX - panStartRef.current.x) / displayScale;
      const deltaY = (e.clientY - panStartRef.current.y) / displayScale;

      onOffsetChange({
        x: offsetStartRef.current.x + deltaX,
        y: offsetStartRef.current.y + deltaY,
      });
    },
    [isPanning, hasBackgroundImage, displayScale, onOffsetChange]
  );

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Calculate distance between two touch points
  const getTouchDistance = (touches: TouchList): number => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Calculate center point between two touches
  const getTouchCenter = (touches: TouchList): { x: number; y: number } => {
    if (touches.length < 2) {
      return { x: touches[0].clientX, y: touches[0].clientY };
    }
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    };
  };

  // Handle touch start
  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!hasBackgroundImage || isStickerModeActive) return;

      if (e.touches.length === 2 && !isElementSelected && !isDraggingElement) {
        // Two-finger gesture - prepare for pinch zoom
        e.preventDefault();
        lastTouchDistanceRef.current = getTouchDistance(e.touches);
        lastTouchCenterRef.current = getTouchCenter(e.touches);
        offsetStartRef.current = { ...backgroundImageOffset };
      } else if (e.touches.length === 1 && !isElementSelected && !isDraggingElement) {
        // Single finger pan (when no element selected)
        setIsPanning(true);
        panStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        offsetStartRef.current = { ...backgroundImageOffset };
      }
    },
    [hasBackgroundImage, isStickerModeActive, isElementSelected, isDraggingElement, backgroundImageOffset]
  );

  // Handle touch move
  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!hasBackgroundImage || isStickerModeActive || displayScale === 0) return;

      if (e.touches.length === 2 && lastTouchDistanceRef.current !== null && !isElementSelected && !isDraggingElement) {
        // Pinch to zoom
        e.preventDefault();

        const currentDistance = getTouchDistance(e.touches);
        const currentCenter = getTouchCenter(e.touches);

        // Calculate zoom
        const distanceDelta = currentDistance - lastTouchDistanceRef.current;
        const scaleDelta = distanceDelta * PINCH_SENSITIVITY;
        const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, backgroundImageScale * (1 + scaleDelta)));

        // Calculate pan from center movement
        if (lastTouchCenterRef.current) {
          const panDeltaX = (currentCenter.x - lastTouchCenterRef.current.x) / displayScale;
          const panDeltaY = (currentCenter.y - lastTouchCenterRef.current.y) / displayScale;

          // Zoom towards pinch center
          const { x: canvasX, y: canvasY } = screenToCanvas(currentCenter.x, currentCenter.y);
          const centerX = canvasWidth / 2;
          const centerY = canvasHeight / 2;
          const dx = canvasX - centerX;
          const dy = canvasY - centerY;
          const scaleRatio = newScale / backgroundImageScale;

          const newOffsetX = backgroundImageOffset.x + panDeltaX - dx * (scaleRatio - 1);
          const newOffsetY = backgroundImageOffset.y + panDeltaY - dy * (scaleRatio - 1);

          onScaleChange(newScale);
          onOffsetChange({ x: newOffsetX, y: newOffsetY });
        }

        lastTouchDistanceRef.current = currentDistance;
        lastTouchCenterRef.current = currentCenter;
      } else if (e.touches.length === 1 && isPanning) {
        // Single finger pan
        const deltaX = (e.touches[0].clientX - panStartRef.current.x) / displayScale;
        const deltaY = (e.touches[0].clientY - panStartRef.current.y) / displayScale;

        onOffsetChange({
          x: offsetStartRef.current.x + deltaX,
          y: offsetStartRef.current.y + deltaY,
        });
      }
    },
    [
      hasBackgroundImage,
      isStickerModeActive,
      isPanning,
      isElementSelected,
      isDraggingElement,
      backgroundImageScale,
      backgroundImageOffset,
      displayScale,
      canvasWidth,
      canvasHeight,
      onScaleChange,
      onOffsetChange,
      screenToCanvas,
    ]
  );

  // Handle touch end
  const handleTouchEnd = useCallback(() => {
    setIsPanning(false);
    lastTouchDistanceRef.current = null;
    lastTouchCenterRef.current = null;
  }, []);

  // Set up event listeners
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Wheel event for zoom
    canvas.addEventListener('wheel', handleWheel, { passive: false });

    // Mouse events for pan
    canvas.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    // Touch events
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [
    canvasRef,
    handleWheel,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  ]);

  return {
    isPanning,
    displayScale,
  };
}
